import { createServerFn } from "@tanstack/react-start";
import * as Sentry from "@sentry/cloudflare";
import { requireSessionUser } from "@/lib/serverAuth";
import { deleteUserIngredient } from "@/lib/ingredients/mutations.server";
import { DomainConflictError } from "@/lib/domain/errors";
import {
  getUserIngredientUsageRecipes,
  type IngredientUsageRecipeLink,
} from "@/lib/ingredients/ingredientOverview.server";

export type DeleteIngredientResult =
  | { deleted: true }
  | {
      deleted: false;
      reason: "ingredient-in-use";
      recipes: IngredientUsageRecipeLink[];
    };

export const deleteIngredient = createServerFn({ method: "POST" })
  .inputValidator((data: { ingredientId: number; force?: boolean }) => data)
  .handler(async ({ data }): Promise<DeleteIngredientResult> => {
    const user = await requireSessionUser();

    try {
      await deleteUserIngredient({
        selector: { id: data.ingredientId },
        userId: user.id,
        force: data.force,
      });
      return { deleted: true };
    } catch (error) {
      if (error instanceof DomainConflictError) {
        const recipes = await getUserIngredientUsageRecipes({
          ingredientId: data.ingredientId,
          userId: user.id,
        });
        return { deleted: false, reason: "ingredient-in-use", recipes };
      }

      console.error(error);
      Sentry.captureException(error);
      throw new Error("Failed to delete ingredient");
    }
  });
